import type { ScopedProject } from "@domain/project/project-guards";
import Excel, { Cell, Fill } from "exceljs";
import { ProductListTheme } from "./export-product-list.theme";

export type ProductListLayoutRow = {
  row: number;
  height?: number;
  font?: Partial<Excel.Font>;
};

export type ProductListLayoutCell = {
  address: string;
  value?: Cell["value"];
  fill?: Fill;
  border?: Partial<Excel.Borders>;
  alignment?: Partial<Excel.Alignment>;
  font?: Partial<Excel.Font>;
  numFmt?: string;
  validationOptions?: string;
};

export type ProductListLayoutBlock = {
  merges?: string[];
  rows?: ProductListLayoutRow[];
  cells: ProductListLayoutCell[];
};

export type ProductListSheetFormat = {
  rowHeights?: Array<{ row: number; height: number }>;
  columns: Array<{
    column: number;
    width?: number;
    hidden?: boolean;
    alignment?: Partial<Excel.Alignment>;
    numFmt?: string;
  }>;
};

const FIRST_PRODUCT_LIST_DATA_ROW = 5;
const MATERIAL_MARKER_COLUMN = "L";
const MATERIAL_LINE_MARKER = "MATERIAL_LINE";

export function getProductListHeaderLayout(theme: ProductListTheme): ProductListLayoutBlock {
  const headerAlignment: Partial<Excel.Alignment> = {
    vertical: "middle",
    horizontal: "center",
  };
  return {
    rows: [{ row: 4, height: 32, font: theme.fonts.bold }],
    cells: [
      { address: "A4", value: $localize`:@@productListExport.column1.header:Pos.`, alignment: headerAlignment },
      { address: "B4", value: $localize`:@@productListExport.column2.header:Sachnummer`, alignment: headerAlignment },
      { address: "C4", value: $localize`:@@productListExport.column3.header:Anzahl`, alignment: headerAlignment },
      { address: "D4", value: $localize`:@@productListExport.column4.header:Bezeichnung`, alignment: headerAlignment },
      { address: "E4", value: $localize`:@@productListExport.column5.header:Einheit`, alignment: headerAlignment },
      { address: "F4", value: $localize`:@@productListExport.column6.header:Listenpreis`, alignment: headerAlignment },
      { address: "G4", value: $localize`:@@productListExport.column7.header:Rabatt`, alignment: headerAlignment },
      { address: "H4", value: $localize`:@@productListExport.column8.header:Einzelpreis`, alignment: headerAlignment },
      { address: "I4", value: $localize`:@@productListExport.column9.header:Gesamtpreis`, alignment: headerAlignment },
    ],
  };
}

export function getProductListAddressLayout(
  project: ScopedProject,
  theme: ProductListTheme,
  lineBreak: string,
): ProductListLayoutBlock {
  const borderLeft = {
    top: theme.borders.thick,
    bottom: theme.borders.thick,
    left: theme.borders.thick,
  };
  const borderRight = {
    top: theme.borders.thick,
    bottom: theme.borders.thick,
    right: theme.borders.thick,
  };
  const borderMid = { top: theme.borders.thick, bottom: theme.borders.thick };

  const dropdownOptionExZone = $localize`:@@productListExport.dropdownOptions.exZone:keine Ex-Zone, Ex-Zone 0, Ex-Zone 1, Ex-Zone 2`;
  const dropdownOptionShowDiscounts = $localize`:@@productListExport.dropdownOptions.showDiscounts:Ja, Nein`;
  const dropdownOptionDustZone = $localize`:@@productListExport.dropdownOptions.dustZone:keine Staub-Zone, Staub-Zone 20, Staub-Zone 21, Staub-Zone 22`;
  const dropdownOptionInfoSSB = $localize`:@@productListExport.dropdownOptions.infoSSB:Ja, Nein`;

  const addressValue =
    (project.contactPerson.firstname ? project.contactPerson.firstname + " " : "") +
    (project.contactPerson.lastname ? project.contactPerson.lastname + lineBreak : "") +
    (project.contactPerson.department ? project.contactPerson.department + lineBreak : "") +
    (project.contactPerson.email ? project.contactPerson.email + lineBreak : "") +
    (project.contactPerson.phoneNumber ? "Tel: " : "") +
    (project.contactPerson.phoneNumber ? project.contactPerson.phoneNumber + lineBreak + lineBreak : "") +
    (project.customer.name ? project.customer.name + lineBreak : "") +
    (project.customer.street ? project.customer.street + lineBreak : "") +
    (project.customer.zipCode ? project.customer.zipCode + " " : "") +
    (project.customer.city ? project.customer.city : "");

  return {
    merges: ["A1:C1", "A2:C2", "A3:C3", "F1:G1", "F2:G2", "D3:I3"],
    rows: [
      { row: 1, font: theme.fonts.bold },
      { row: 2, font: theme.fonts.bold },
    ],
    cells: [
      {
        address: "A1",
        value: $localize`:@@productListExport.projectName.prefix:Projektname:`,
        fill: theme.fills.header,
        border: borderLeft,
      },
      { address: "D1", value: project.name, border: borderRight },
      {
        address: "E1",
        value: $localize`:@@productListExport.requestDate.prefix:Anfragedatum:`,
        fill: theme.fills.header,
        border: borderLeft,
      },
      { address: "F1", fill: theme.fills.subHeader, border: borderMid, numFmt: "dd.MM.yyyy" },
      {
        address: "H1",
        value: $localize`:@@productListExport.exZone.prefix:Ex-Zone:`,
        fill: theme.fills.header,
        border: borderMid,
      },
      { address: "I1", fill: theme.fills.subHeader, border: borderRight, validationOptions: dropdownOptionExZone },
      {
        address: "J1",
        value: $localize`:@@productListExport.showDiscounts.prefix:Rabatte anzeigen:`,
        fill: theme.fills.header,
        border: borderMid,
      },
      { address: "K1", fill: theme.fills.subHeader, border: borderRight, validationOptions: dropdownOptionShowDiscounts },
      {
        address: "A2",
        value: $localize`:@@productListExport.customerNumber.prefix:KD-Nr.:`,
        fill: theme.fills.header,
        border: borderLeft,
      },
      { address: "D2", value: project.customer.customerNumber, border: borderRight },
      { address: "E2", fill: theme.fills.header, border: borderLeft },
      { address: "F2", fill: theme.fills.subHeader, border: borderMid },
      {
        address: "H2",
        value: $localize`:@@productListExport.dustZone.prefix:Staub-Zone:`,
        fill: theme.fills.header,
        border: borderMid,
      },
      { address: "I2", fill: theme.fills.subHeader, border: borderRight, validationOptions: dropdownOptionDustZone },
      {
        address: "J2",
        value: $localize`:@@productListExport.infoSSB.prefix:Info SSB vorhanden:`,
        fill: theme.fills.header,
        border: borderMid,
      },
      { address: "K2", fill: theme.fills.subHeader, border: borderRight, validationOptions: dropdownOptionInfoSSB },
      {
        address: "A3",
        value: $localize`:@@productListExport.address.prefix:Anschrift:`,
        border: borderLeft,
        alignment: { vertical: "top", horizontal: "right" },
        font: theme.fonts.normal,
      },
      { address: "C3", border: borderMid },
      {
        address: "D3",
        value: addressValue,
        border: borderRight,
        alignment: { vertical: "top" },
        font: theme.fonts.normal,
      },
      {
        address: "J3",
        value: $localize`:@@productListExport.salesChannel.prefix:Vertriebsweg 03:`,
        fill: theme.fills.header,
        border: borderLeft,
        alignment: { vertical: "top" },
      },
      {
        address: "K3",
        value: $localize`:@@productListExport.technicianName.prefix:Name Techniker`,
        fill: theme.fills.subHeader,
        border: borderRight,
        alignment: { vertical: "top" },
      },
    ],
  };
}

export function getProductListChecklistLayout(startRow: number, theme: ProductListTheme): ProductListLayoutBlock {
  const checklist = [
    {
      label: $localize`:@@productListExport.checklist.item1:Bindesfrist Angebot`,
      options: $localize`:@@productListExport.checklist.dropdownOptions.item1:2 Wochen, 4 Wochen, 3 Monate, 12 Monate, Ende des aktuellen Jahres, Benutzerdefiniert`,
    },
    {
      label: $localize`:@@productListExport.checklist.item2:Angebot nach VOB`,
      options: $localize`:@@productListExport.checklist.dropdownOptions.item2:Ja, Nein`,
    },
    {
      label: $localize`:@@productListExport.checklist.item3:Angebotsnachverfolgung durch IVI/VI/Callcenter FGDS?`,
      options: $localize`:@@productListExport.checklist.dropdownOptions.item3:IVI, VI, Callcenter FGDS`,
    },
    {
      label: $localize`:@@productListExport.checklist.item4:Gewährleistungsfristen`,
      options: $localize`:@@productListExport.checklist.dropdownOptions.item4:nach VOB, Standard`,
    },
    { label: $localize`:@@productListExport.checklist.item5:Endbestimmungsland` },
    {
      label: $localize`:@@productListExport.checklist.item6:Wiedervorlage nach`,
      options: $localize`:@@productListExport.checklist.dropdownOptions.item6:2 Wochen, 4 Wochen, 3 Monate, 12 Monate, Ende des aktuellen Jahres, Benutzerdefiniert`,
    },
    {
      label: $localize`:@@productListExport.checklist.item7:Sollen dem Angebot Produktinformationen beigefügt werden`,
      options: $localize`:@@productListExport.checklist.dropdownOptions.item7:Ja, Nein`,
    },
    {
      label: $localize`:@@productListExport.checklist.item8:Angebotsversendung durch...`,
      options: $localize`:@@productListExport.checklist.dropdownOptions.item8:SB, VI, IVI`,
    },
    {
      label: $localize`:@@productListExport.checklist.item9:Benutzerdefinierte Bindefrist und/oder Wiedervorlage (Freitext)`,
    },
  ];

  const merges = [startRow, ...checklist.map((_, index) => startRow + 1 + index)].map((row) => `E${row}:H${row}`);

  const cells: ProductListLayoutCell[] = [
    {
      address: `D${startRow}`,
      value: $localize`:@@productListExport.checklist.header:Benötigte Informationen vom VI`,
      border: {
        top: theme.borders.thin,
        left: theme.borders.thin,
        bottom: theme.borders.thin,
        right: theme.borders.thin,
      },
      font: theme.fonts.bold,
      fill: theme.fills.grey,
    },
    {
      address: `E${startRow}`,
      border: {
        top: theme.borders.thin,
        bottom: theme.borders.thin,
        right: theme.borders.thin,
      },
      fill: theme.fills.grey,
    },
  ];

  checklist.forEach((item, index) => {
    const row = startRow + 1 + index;
    cells.push(
      {
        address: `D${row}`,
        value: item.label,
        border: {
          left: theme.borders.thin,
          bottom: theme.borders.thin,
          right: theme.borders.thin,
        },
        font: theme.fonts.normal,
      },
      {
        address: `E${row}`,
        border: {
          bottom: theme.borders.thin,
          right: theme.borders.thin,
        },
        font: theme.fonts.bold,
        validationOptions: item.options,
      },
    );
  });

  return {
    merges,
    cells,
  };
}

export function getProductListTotalsLayout(
  startRow: number,
  firstRowOfServices: number,
  lastRowOfServices: number,
  theme: ProductListTheme,
  areCurrenciesMixed = false,
): ProductListLayoutBlock {
  const materialRow = startRow;
  const servicesRow = startRow + 1;
  const sumNetRow = startRow + 2;
  const vatRow = startRow + 3;
  const sumTotalRow = startRow + 4;
  const materialEndRow = Math.max(firstRowOfServices - 1, FIRST_PRODUCT_LIST_DATA_ROW);
  const cannotBeCalculated = "";

  return {
    rows: [
      { row: materialRow, font: theme.fonts.normal },
      { row: servicesRow, font: theme.fonts.normal },
    ],
    cells: [
      {
        address: `D${materialRow}`,
        value: $localize`:@@global.material:Material`,
        border: { left: theme.borders.thick, top: theme.borders.thick },
      },
      { address: `E${materialRow}`, border: { top: theme.borders.thick } },
      { address: `F${materialRow}`, border: { top: theme.borders.thick } },
      { address: `G${materialRow}`, border: { top: theme.borders.thick } },
      { address: `H${materialRow}`, border: { top: theme.borders.thick } },
      {
        address: `I${materialRow}`,
        value: {
          formula: `SUMIFS(I${FIRST_PRODUCT_LIST_DATA_ROW}:I${materialEndRow},${MATERIAL_MARKER_COLUMN}${FIRST_PRODUCT_LIST_DATA_ROW}:${MATERIAL_MARKER_COLUMN}${materialEndRow},"${MATERIAL_LINE_MARKER}")`,
        },
        border: { right: theme.borders.thick, top: theme.borders.thick },
      },
      {
        address: `D${servicesRow}`,
        value: $localize`:@@global.services:Dienstleistungen`,
        border: { left: theme.borders.thick },
      },
      {
        address: `I${servicesRow}`,
        value: { formula: `SUM(I${firstRowOfServices}:I${lastRowOfServices})` },
        border: { right: theme.borders.thick },
      },
      {
        address: `D${sumNetRow}`,
        value: $localize`:@@productListExport.sumNet:Summe Netto`,
        border: { left: theme.borders.thick },
      },
      {
        address: `I${sumNetRow}`,
        value: areCurrenciesMixed ? cannotBeCalculated : { formula: `I${sumNetRow - 2}+I${sumNetRow - 1}` },
        border: { right: theme.borders.thick },
      },
      {
        address: `D${vatRow}`,
        value: $localize`:@@productListExport.vat:MwSt`,
        border: { left: theme.borders.thick },
      },
      {
        address: `I${vatRow}`,
        value: areCurrenciesMixed ? cannotBeCalculated : { formula: `I${vatRow - 1}*0.19` },
        border: { right: theme.borders.thick },
      },
      {
        address: `D${sumTotalRow}`,
        value: $localize`:@@productListExport.sumTotal:Summe Brutto`,
        border: { left: theme.borders.thick, bottom: theme.borders.thick },
      },
      { address: `E${sumTotalRow}`, border: { bottom: theme.borders.thick } },
      { address: `F${sumTotalRow}`, border: { bottom: theme.borders.thick } },
      { address: `G${sumTotalRow}`, border: { bottom: theme.borders.thick } },
      { address: `H${sumTotalRow}`, border: { bottom: theme.borders.thick } },
      {
        address: `I${sumTotalRow}`,
        value: areCurrenciesMixed ? cannotBeCalculated : { formula: `I${sumTotalRow - 2}+I${sumTotalRow - 1}` },
        border: { right: theme.borders.thick, bottom: theme.borders.thick },
      },
    ],
  };
}

export function getProductListSheetFormat(currencyFormat: string, percentFormat: string): ProductListSheetFormat {
  return {
    columns: [
      { column: 1, alignment: { horizontal: "right" } },
      { column: 2, width: 16, alignment: { horizontal: "center" } },
      { column: 3, alignment: { horizontal: "right" } },
      { column: 4, width: 64 },
      { column: 5, width: 16, alignment: { horizontal: "center" } },
      { column: 6, width: 16, numFmt: currencyFormat },
      { column: 7, alignment: { horizontal: "center" }, numFmt: percentFormat },
      { column: 8, width: 16, numFmt: currencyFormat },
      { column: 9, width: 16, numFmt: currencyFormat },
      { column: 10, width: 20 },
      { column: 11, width: 16 },
      { column: 12, hidden: true },
    ],
  };
}
